import { useInfiniteQuery } from '@tanstack/react-query';

import { resolve } from 'api/resolve';

import { CONNECTED_NODES_GRID_PAGE_SIZE, getNextConnectedNodesOffset } from '../connectedNodesPaging';
import { useProjectNodesApi } from '../project-nodes-api';

export const PROJECT_CONNECTED_NODES_LIST_QUERY_KEY = 'projectConnectedNodesList';

/**
 * The identifying prefix of every `useConnectedDataNodes` cache entry in a project, regardless of
 * which relation or sort variant it holds (those trail the key — see the full `queryKey` below).
 * Callers that need to patch every cached page-list a node might appear in (e.g. an in-cell edit)
 * should match on this prefix rather than duplicating the literal key. Kept deliberately coarse: the
 * same data node can be a member of several relations, so an edit has to reach all of them, and a
 * page-level updater no-ops on pages that don't contain the node anyway.
 */
export function getConnectedDataNodesQueryKeyPrefix(projectId: string | undefined, proposalId: string | undefined) {
  return [PROJECT_CONNECTED_NODES_LIST_QUERY_KEY, projectId, proposalId] as const;
}

interface UseConnectedDataNodesArgs {
  projectId: string | undefined;
  proposalId: string | undefined;
  /** The node the members hang off (`parentDataNodeId`). */
  parentId: string | undefined;
  /** Technical name of the parent's type (`parentNodeTechnicalName`). */
  parentTechnicalName: string | undefined;
  /** Technical name of the connected type being listed. */
  technicalName: string | undefined;
  relationId: string | undefined;
  /** Whether the relation points *at* the parent type. */
  isReverse: boolean;
  /**
   * Property to sort by. Required by the backend, so callers must resolve "no sort" to something
   * stable (`Id`) rather than omitting it — otherwise the effective sort wouldn't appear in the key.
   */
  orderBy: string;
  orderDirection: 'Asc' | 'Desc';
  enabled?: boolean;
}

/**
 * List the members of one blueprint relation as a paginated collection — the list counterpart to the
 * graph's single capped `loadRelationMembers` fetch over the same endpoint.
 *
 * The key is deliberately **disjoint** from the graph's `connectedNodesQueryKey`, and the two must
 * not be merged. That key holds a bare `DataNode[]` (one page, written imperatively by
 * `queryClient.fetchQuery` and shared on purpose with `probeRelationHasMembers`), whereas this holds
 * an `InfiniteData<DataNode[]>`. Sharing the key would have the two overwrite each other with
 * structurally different values — a runtime fault TypeScript wouldn't catch. Priming this query from
 * that cache is wrong too: the graph pins `orderBy=Id` at a 100-row limit, so a page shorter than
 * this hook's page size would read as "end of list" and silently truncate the list. The cost of
 * keeping them apart is one duplicated first-page request per relation.
 */
export function useConnectedDataNodes({
  projectId,
  proposalId,
  parentId,
  parentTechnicalName,
  technicalName,
  relationId,
  isReverse,
  orderBy,
  orderDirection,
  enabled = true
}: UseConnectedDataNodesArgs) {
  const { getConnectedNodes } = useProjectNodesApi();

  const isResolved = Boolean(projectId && parentId && parentTechnicalName && technicalName && relationId);

  return useInfiniteQuery({
    queryKey: [
      ...getConnectedDataNodesQueryKeyPrefix(projectId, proposalId),
      parentId,
      relationId,
      technicalName,
      isReverse,
      orderBy,
      orderDirection
    ],
    queryFn: ({ pageParam }) => {
      // Unreachable while `enabled` holds, but narrowing here rather than returning `[]` keeps an
      // empty page from ever being cached under a key the blueprint's arrival would never invalidate.
      if (!projectId || !parentId || !parentTechnicalName || !technicalName || !relationId) {
        throw new Error('useConnectedDataNodes ran before its scope resolved.');
      }
      return resolve(
        getConnectedNodes(projectId, parentId, {
          parentNodeTechnicalName: parentTechnicalName,
          relationId,
          technicalName,
          isReverse,
          offset: pageParam,
          limit: CONNECTED_NODES_GRID_PAGE_SIZE,
          orderBy,
          orderDirection,
          proposalId
        })
      );
    },
    initialPageParam: 0,
    getNextPageParam: getNextConnectedNodesOffset,
    enabled: isResolved && enabled
  });
}
